<script setup lang="ts">
import { ref, reactive, computed, watch, nextTick, onMounted, onBeforeUnmount } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useProductDetail, useSaveProductDetailMutation, useRefreshProductFromCatalogMutation, useCheckNgWords } from '@/api/queries'
import { useAuthStore } from '@/stores/auth'
import { useConfirmModalStore } from '@/stores/confirmModal'
import { useResultModalStore } from '@/stores/resultModal'
import { useNgMarkStore } from '@/stores/ngMark'
import { levelBucket } from '@/utils/ngHighlight'
import { openExternal } from '@/utils/links'
import TopBar from '@/components/TopBar.vue'
import ProductImagePanel from '@/components/ProductImagePanel.vue'
import NgMarkedText from '@/components/NgMarkedText.vue'
import NgLevelFilter from '@/components/NgLevelFilter.vue'
import NgHitDetailPane from '@/components/NgHitDetailPane.vue'
import PaneDivider from '@/components/PaneDivider.vue'
import { useLayoutStore } from '@/stores/layout'
import type { ProductFull } from '@/types/api'
import JudgmentQueueApplyModal from '@/components/JudgmentQueueApplyModal.vue'

const showJudgmentQueueModal = ref(false)

const route = useRoute()
const router = useRouter()
const auth = useAuthStore()
const confirmModal = useConfirmModalStore()
const resultModal = useResultModalStore()
const ngMark = useNgMarkStore()
const layout = useLayoutStore()
const isLeftVisible = computed(() => layout.leftPaneVisible.productEdit)

// URL(?asin=XXXX)から初期値を復元。無ければ空(単独起動の検索待ち状態)。
const searchAsin = ref((route.query.asin as string) ?? '')
// 国コードはUIでは選択させない(実質jpのみのため)。DB検索時は常に固定でjpを使う。
const REGION_CODE = 'jp'
const regionCode = ref(REGION_CODE)

// 実際に検索が確定したASIN(入力中の値とは分けて、Enter/検索ボタン押下時のみ反映する)
const activeAsin = ref<string | null>((route.query.asin as string) || null)

const { data, isLoading, isError, error, refetch } = useProductDetail(activeAsin, regionCode)

function retryFetch() {
  refetch()
}

function runSearch() {
  const asin = searchAsin.value.trim().toUpperCase()
  if (!asin) return
  activeAsin.value = asin
  // URLにも反映しておく(ブックマーク・共有・リロード耐性のため)
  router.replace({ query: { asin } })
}

// 外部(審査Webのリンク等)から ?asin= 付きで直接開かれた場合にも追従する
watch(
  () => route.query.asin,
  (v) => {
    if (typeof v === 'string' && v !== activeAsin.value) {
      searchAsin.value = v
      activeAsin.value = v
    }
  },
)

const product = computed(() => data.value?.product ?? null)
const categories = computed(() => data.value?.categories ?? [])

// ---- 編集/参照モード(VB版には無いが新規追加。デフォルトは参照のみ) ----
const isEditMode = ref(false)

// ---- 編集フォーム(VB版 SaveProduct() が扱う項目一式) ----
interface EditForm {
  categoryCode: string
  binding: string
  brand: string
  ean: string
  model: string
  title: string
  height: string
  length: string
  width: string
  packageWeight: string
  itemWeight: string
  releaseDate: string
  size: string
  color: string
  listPrice: string
  salesRank: string
  editorialReview: string
  features: string
  adultFlg: boolean
  rating: string
}

function blankForm(): EditForm {
  return {
    categoryCode: '', binding: '', brand: '', ean: '', model: '', title: '',
    height: '', length: '', width: '', packageWeight: '', itemWeight: '',
    releaseDate: '', size: '', color: '', listPrice: '', salesRank: '',
    editorialReview: '', features: '', adultFlg: false, rating: '',
  }
}

const form = reactive<EditForm>(blankForm())

const categoryName = computed(
  () => categories.value.find((c) => c.CategoryCode === form.categoryCode)?.CategoryName ?? form.categoryCode,
)

/**
 * DBの値をフォームへ反映する。
 * あわせて「保存済みの状態」としてスナップショットを取り、未保存判定の基準にする。
 */
function applyProductToForm(p: ProductFull) {
  form.categoryCode = p.CategoryCode ?? ''
  form.binding = p.Binding ?? ''
  form.brand = p.Brand ?? ''
  form.ean = p.EAN ?? ''
  form.model = p.Model ?? ''
  form.title = p.Title ?? ''
  form.height = p.Height?.toString() ?? ''
  form.length = p.Length?.toString() ?? ''
  form.width = p.Width?.toString() ?? ''
  form.packageWeight = p.PackageWeight?.toString() ?? ''
  form.itemWeight = p.ItemWeight?.toString() ?? ''
  form.releaseDate = p.ReleaseDate ?? ''
  form.size = p.Size ?? ''
  form.color = p.Color ?? ''
  form.listPrice = p.ListPrice?.toString() ?? ''
  form.salesRank = p.SalesRank?.toString() ?? ''
  form.editorialReview = p.EditorialReview ?? ''
  form.features = p.Features ?? ''
  form.adultFlg = p.AdultFlg === '1'
  form.rating = p.Rating?.toString() ?? ''
  savedSnapshot.value = JSON.stringify(form)
}

// 商品データが読み込まれる(=新しいASINを検索する)たびに、編集フォームへ反映し直す
watch(product, (p) => {
  if (!p) {
    Object.assign(form, blankForm())
    savedSnapshot.value = JSON.stringify(form)
    return
  }
  applyProductToForm(p)

  // VB版は商品ロード時(コンストラクタ/ButtonProductSearch_Click)に必ずNGワード
  // 自動チェックを行っている(Loadedイベントでも同様)。Web版もこれに合わせて
  // 商品を読み込むたびに自動でチェックする(手動の「NGワードチェック」ボタンは
  // 編集中に再チェックしたい場合の補助として残す)。
  onCheckNgWords()
}, { immediate: true })

/**
 * 未保存の変更があるか。
 *
 * 【なぜ必要か】参照モードの表示は form の値を映しているため、編集後に保存せず
 * 編集モードを抜けても画面上は編集後の値が見え、「保存済み」と誤解しやすい。
 * 実際にDBへ書かれるのは「保存」を押した瞬間だけなので、その差を明示する。
 */
const savedSnapshot = ref<string>('')
const isDirty = computed(() => savedSnapshot.value !== '' && JSON.stringify(form) !== savedSnapshot.value)

/**
 * 編集モードの切り替え。未保存の変更を黙って抱えたまま参照モードへ戻ると、
 * 保存したつもりで申請して同じ理由で却下される、といった事故につながるため確認する。
 */
async function toggleEditMode() {
  if (isEditMode.value && isDirty.value) {
    const keep = await confirmModal.ask(
      '未保存の変更があります。\n変更を破棄して編集モードを終了しますか？\n\n' +
      '（「保存」を押すまで、編集内容はDBに反映されません）',
    )
    if (!keep) return
    if (product.value) applyProductToForm(product.value)
  }
  isEditMode.value = !isEditMode.value
}

function toNum(v: string): number | null {
  const t = v.trim()
  if (t === '') return null
  const n = Number(t)
  return Number.isNaN(n) ? null : n
}
function toStrOrNull(v: string): string | null {
  const t = v.trim()
  return t === '' ? null : t
}

// ---- 保存 ----
const saveMutation = useSaveProductDetailMutation()

// Amazonカタログから取得した生JSON項目(Attributes/Keyword/Identifiers/Dimensions)を
// 一時保持する。手動編集フォームでは直接編集しないため、更新(onRefresh)で取得したら
// ここに保持し、保存(onSave)時にDBの既存値より優先して使う。
const fetchedRawFields = ref<{
  attributes: string | null
  keyword: string | null
  identifiers: string | null
  dimensions: string | null
} | null>(null)

async function onSave() {
  if (!activeAsin.value || !product.value) return

  // VB版 ButtonSave_Click は保存確認の前に必ずNGワード再チェックを行う(同じ挙動にする)
  await checkNgWordsAsync({
    title: form.title || undefined,
    model: form.model || undefined,
    color: form.color || undefined,
    size: form.size || undefined,
    features: form.features || undefined,
    description: form.editorialReview || undefined,
  })

  if (!form.categoryCode) {
    resultModal.show('カテゴリを選択してください。', 'ng')
    return
  }
  const sizeSum = (toNum(form.length) ?? 0) + (toNum(form.width) ?? 0) + (toNum(form.height) ?? 0)
  if (sizeSum > 4200) {
    resultModal.show('サイズを調整してください。', 'ng')
    return
  }

  if (!(await confirmModal.ask(`${activeAsin.value} の商品情報をほんとうに上書きしますか？`))) return

  saveMutation.mutate(
    {
      asin: activeAsin.value,
      regionCode: regionCode.value,
      categoryCode: form.categoryCode,
      binding: toStrOrNull(form.binding),
      brand: toStrOrNull(form.brand),
      ean: toStrOrNull(form.ean),
      model: toStrOrNull(form.model),
      title: toStrOrNull(form.title),
      height: toNum(form.height),
      length: toNum(form.length),
      width: toNum(form.width),
      packageWeight: toNum(form.packageWeight),
      itemWeight: toNum(form.itemWeight),
      releaseDate: toStrOrNull(form.releaseDate),
      size: toStrOrNull(form.size),
      color: toStrOrNull(form.color),
      listPrice: toNum(form.listPrice),
      salesRank: toNum(form.salesRank),
      editorialReview: toStrOrNull(form.editorialReview),
      editorialReviewLinkFlg: product.value.EditorialReviewLinkFlg,
      features: toStrOrNull(form.features),
      adultFlg: form.adultFlg,
      rating: toNum(form.rating),
      attributes: fetchedRawFields.value?.attributes ?? product.value.Attributes,
      keyword: fetchedRawFields.value?.keyword ?? product.value.Keyword,
      identifiers: fetchedRawFields.value?.identifiers ?? product.value.Identifiers,
      dimensions: fetchedRawFields.value?.dimensions ?? product.value.Dimensions,
    },
    {
      onSuccess: () => {
        // 保存が通った時点の内容を「保存済み」の基準に更新する(未保存表示を消す)
        savedSnapshot.value = JSON.stringify(form)
        resultModal.show(`${activeAsin.value} の商品情報を上書きしました。`)
      },
      onError: (err) => resultModal.show((err as Error).message, 'ng'),
    },
  )
}

// ---- Amazonカタログから更新 ----
const refreshMutation = useRefreshProductFromCatalogMutation()

async function onRefresh() {
  if (!activeAsin.value) return
  if (!(await confirmModal.ask(`${activeAsin.value} の商品情報をAmazonカタログの最新情報で上書きします。よろしいですか？`)))
    return

  refreshMutation.mutate(
    { asin: activeAsin.value, req: { regionCode: regionCode.value, operator: auth.displayName } },
    {
      onSuccess: (res) => {
        // 取得結果を編集フォームへ反映する(VB版 SetUi 相当。DB保存はしない)
        form.title = res.title ?? form.title
        form.brand = res.brand ?? form.brand
        form.editorialReview = res.editorialReview ?? form.editorialReview
        form.features = res.features ?? form.features
        form.color = res.color ?? form.color
        form.size = res.size ?? form.size
        form.releaseDate = res.releaseDate ?? form.releaseDate
        form.salesRank = res.salesRank?.toString() ?? form.salesRank
        form.listPrice = res.listPrice?.toString() ?? form.listPrice
        form.adultFlg = res.adultFlg

        fetchedRawFields.value = {
          attributes: res.attributes,
          keyword: res.keyword,
          identifiers: res.identifiers,
          dimensions: res.dimensions,
        }

        resultModal.show(
          `Amazonカタログから情報を取得しました。内容を確認し、問題なければ「保存」を押してください。`,
        )
      },
      // バックエンドのメッセージが既に具体的な内容を含むため、フロント側で
      // 重ねて同じ内容を前置きしない(以前は「〜取得に失敗しました: 〜取得に失敗しました。」
      // のように同じ意味の文言が二重に表示されていた)。
      onError: (err) => resultModal.show((err as Error).message, 'ng'),
    },
  )
}

// ---- NGワードチェック ----
// VB版 GetNgwordDictionary / ButtonRefreshNgWords 相当。
// 【訂正】当初「手動ボタンのみ」と考えていたが、VB版ソースを精査した結果、
// 実際は (1)ウィンドウ表示時(Loadedイベント) (2)ASIN検索時 (3)保存直前、の
// 3タイミングで自動実行されることが判明した。Web版もこれに合わせ、商品ロード時
// (watch(product,...)内)と保存時(onSave内)に自動実行する。手動ボタンは
// 編集中の任意タイミングでの再チェック用として残す。
const { mutate: checkNgWords, mutateAsync: checkNgWordsAsync, data: ngWordData, isPending: isNgCheckPending } = useCheckNgWords()

// 判定結果をストアへ共有する(審査Webと同じく、本文マーキングと同じデータを使う)
watch(ngWordData, (d) => ngMark.setNgWords(d?.ngWords), { immediate: true })

function onCheckNgWords() {
  checkNgWords({
    title: form.title || undefined,
    model: form.model || undefined,
    color: form.color || undefined,
    size: form.size || undefined,
    features: form.features || undefined,
    description: form.editorialReview || undefined,
  })
}

// NGワード一覧から行を選択したときのハイライト対象。VB版 ChangedDataGridNgwordItems /
// SetRichTextBoxHighlight 相当。参照モード時のみ有効(編集モードのtextareaには
// リッチテキスト表示ができないため)。
const selectedNgMatches = ref<{ field: string; start: number; length: number }[]>([])
const selectedNgLevel = ref<number>(0)
const selectedNgWordName = ref<string | null>(null)

// ---- 商品情報カード内のNGワードチップ ----
// 語だけを重複なく、レベルの高い順に並べる(説明文は左のリスト/ホバーで見る)
const ngWordChips = computed(() => {
  const seen = new Set<string>()
  return ngMark.ngWords
    .filter((w) => {
      if (seen.has(w.word)) return false
      seen.add(w.word)
      return true
    })
    .sort((a, b) => b.level - a.level)
})

/** 同じ語をもう一度押したら選択解除(全ヒットのハイライトに戻す) */
function toggleNgChip(word: string) {
  selectedNgWordName.value = selectedNgWordName.value === word ? null : word
}
const editRoot = ref<HTMLElement | null>(null)

function selectNgWordRow(word: { word: string; level: number; matches?: { field: string; start: number; length: number }[] }) {
  selectedNgMatches.value = word.matches ?? []
  selectedNgLevel.value = word.level
  selectedNgWordName.value = word.word
}
function clearNgWordSelection() {
  selectedNgMatches.value = []
  selectedNgWordName.value = null
}

// ---- NGワードのヒット箇所ナビゲーション(審査Webと同じ「件数・前・次」UI) ----
const matchIndex = ref(0)
const matchCount = ref(0)

/**
 * 選択中のNGワードを含むマーク要素を取得する。
 * 本文は NgMarkedText がセグメント分割して描画しており、旧実装の
 * mark.ng-highlight は存在しない(審査Webと同じ .ng-mark を対象にする)。
 */
function getMarks(): HTMLElement[] {
  if (!editRoot.value || !selectedNgWordName.value) return []
  const all = Array.from(editRoot.value.querySelectorAll<HTMLElement>('.ng-mark'))
  return all.filter((el) =>
    (el.dataset.ngWords ?? '').split('\u001f').includes(selectedNgWordName.value!),
  )
}

/**
 * ハイライトを全消しする。
 * getMarks() は選択中の語のマークしか返さないため、語を切り替えたときに
 * 前の語へ付けたクラスが残ってしまう。消す側は必ず本文全体を対象にする。
 */
function clearHighlight() {
  editRoot.value
    ?.querySelectorAll('.ng-mark-selected, .ng-mark-current')
    .forEach((el) => el.classList.remove('ng-mark-selected', 'ng-mark-current'))
}

function focusMatch(i: number) {
  const marks = getMarks()
  clearHighlight()
  if (marks.length === 0) return
  const idx = (i + marks.length) % marks.length
  matchIndex.value = idx
  // 選択中の語のヒットはすべて黄色背景にし、今いる箇所だけさらに強調する
  marks.forEach((m) => m.classList.add('ng-mark-selected'))
  marks[idx].classList.add('ng-mark-current')
  marks[idx].scrollIntoView({ behavior: 'smooth', block: 'center' })
}
function nextMatch() {
  focusMatch(matchIndex.value + 1)
}
function prevMatch() {
  focusMatch(matchIndex.value - 1)
}

// NGワードを選択(または解除)したタイミングで、ヒット件数を数え直して先頭へ飛ぶ
watch(selectedNgWordName, async () => {
  await nextTick()
  const marks = getMarks()
  matchCount.value = marks.length
  matchIndex.value = 0
  if (selectedNgWordName.value && marks.length > 0) {
    focusMatch(0)
  } else {
    clearHighlight()
  }
})

function ngLevelClass(level: number) {
  if (level >= 9) return 'lv9'
  if (level >= 7) return 'lv7'
  return 'lv5'
}
// ---- workspaceの高さ計算(--actionbar-h)を商品編集画面用にリセット ----
// .workspace の高さは calc(100vh - 42px - var(--actionbar-h, 210px)) で決まる。
// --actionbar-h は審査画面(ActionBar.vue)が固定バーの実測高さを流し込む変数だが、
// 商品編集画面には対応する固定バーが存在しない。審査画面から遷移してきた場合、
// removeProperty()されても既定値210pxにフォールバックしてしまい、
// 存在しないバーの分だけ画面下部に余白ができ、内容が切れる原因になっていた。
// この画面にいる間は明示的に0pxへ固定する。
onMounted(() => {
  document.documentElement.style.setProperty('--actionbar-h', '0px')
})
onBeforeUnmount(() => {
  document.documentElement.style.removeProperty('--actionbar-h')
})
</script>

<template>
  <div id="app-shell" :class="{ 'left-hidden': !isLeftVisible }">
    <TopBar variant="product-edit" />

    <!-- NGワードのヒット詳細(本文のマークにホバーするとその真下にフローティング表示) -->
    <NgHitDetailPane />

    <div
      class="workspace"
      :class="{ 'workspace--left-hidden': !isLeftVisible }"
      :style="isLeftVisible ? { gridTemplateColumns: `${layout.leftPaneWidth.productEdit}px 6px 1fr` } : undefined"
    >
      <!-- 左カラム: ASIN検索 + NGワードチェック一覧(審査Webの左カラムと同じ構成方針) -->
      <div v-show="isLeftVisible" class="col col-left">
        <div class="col-scroll pe-left-scroll">
          <div class="card pe-search-card">
            <div class="field">
              <label>ASIN</label>
              <input
                v-model="searchAsin"
                type="text"
                placeholder="ASINを入力してEnter"
                @keydown.enter="runSearch"
              />
            </div>
            <button class="btn btn-primary" style="width:100%;margin-top:8px;" @click="runSearch">
              <svg class="icon icon-xs" viewBox="0 0 24 24"><circle cx="11" cy="11" r="7"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
              検索
            </button>
          </div>

          <!-- NGワードチェック(VB版 ButtonRefreshNgWords / DataGridNgword 相当) -->
          <div class="card pe-ngword-card">
            <div class="ngword-section pe-ngword-section">
              <div class="ngword-head">
                <div class="section-title">
                  <svg class="icon" viewBox="0 0 24 24"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                  NGワードチェック
                </div>
                <button class="ng-refresh" title="現在の入力内容でチェック" :disabled="isNgCheckPending || !product" @click="onCheckNgWords">
                  <svg class="icon icon-sm" viewBox="0 0 24 24"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>
                </button>
              </div>
              <div class="ngword-body">
                <div v-if="isNgCheckPending" class="empty-state"><span class="spinner"></span> 判定中…</div>
                <div v-else-if="!product" class="empty-state" style="padding:14px 0;">
                  ASINを検索すると、自動で判定します。
                </div>
                <div v-else-if="!ngWordData" class="empty-state" style="padding:14px 0;">
                  「NGワードチェック」の更新アイコンを押すと、現在の入力内容を判定します。
                </div>
                <div v-else-if="ngWordData.ngWords.length === 0" class="ng-empty">
                  <svg class="icon" viewBox="0 0 24 24"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                  該当するNGワードはありません
                </div>
                <div
                  v-else
                  class="ngrow"
                  :class="[ngLevelClass(w.level), { selected: selectedNgMatches === w.matches }]"
                  v-for="w in ngWordData.ngWords"
                  :key="w.word"
                  @click="selectNgWordRow(w)"
                >
                  <div class="ngrow-word">{{ w.word }}</div>
                  <div class="ngrow-reason">{{ w.reason }}</div>
                  <div class="ngrow-side">
                    <span class="ngrow-hits" :title="'ヒット箇所数'">{{ w.matches?.length ?? 0 }}</span>
                    <a v-if="w.link" class="ngrow-link" title="参考リンクを開く" @click.stop="openExternal(w.link)">
                      <svg class="icon icon-xs" viewBox="0 0 24 24"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>
                    </a>
                    <span class="ngrow-level">Lv.{{ w.level }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <PaneDivider v-if="isLeftVisible" screen="productEdit" />

      <!-- 右カラム: 上に画像、下に商品情報(審査Webの右カラムと同じ構成方針) -->
      <div class="col col-center">
        <div class="col-scroll pe-right-scroll" ref="editRoot">
          <div v-if="!activeAsin" class="card empty-state" style="text-align:center;padding:48px 0;">
            左側でASINを入力して検索してください。
          </div>

          <div v-else-if="isLoading" class="card empty-state" style="text-align:center;padding:48px 0;">
            <span class="spinner"></span> 読み込み中…
          </div>

          <div v-else-if="isError" class="card empty-state" style="text-align:center;padding:48px 0;color:var(--ng);">
            商品情報の取得に失敗しました: {{ (error as Error)?.message }}
            <div style="margin-top:10px;"><button class="btn btn-ghost btn-sm" @click="retryFetch">再試行</button></div>
          </div>

          <div v-else-if="!product" class="card empty-state" style="text-align:center;padding:48px 0;">
            {{ activeAsin }} の商品情報が見つかりませんでした。
          </div>

          <template v-else>
            <!-- 商品画像は全幅(審査Webと同じ。枚数が多くても横スクロールしにくいように) -->
            <ProductImagePanel :asin="activeAsin" :region-code="regionCode" />

            <!-- 対比ゾーン: 左=商品情報 / 右=拡大画像
                 画像と本文を並べて見られるようにする(審査Webと同じ構成) -->
            <div class="detail-compare detail-compare--single">
            <div class="detail-main">

            <div class="card">
              <div class="card-head card-head--left">
                <div class="section-title">
                  <svg class="icon" viewBox="0 0 24 24"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>
                  {{ product.ASIN }}
                </div>
                <!-- 編集モードの切り替えはASINの直後に置く(右端に離すと関連が分かりにくいため) -->
                <button class="btn btn-ghost btn-sm pe-mode-toggle" @click="toggleEditMode">
                  <svg class="icon icon-xs" viewBox="0 0 24 24"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                  {{ isEditMode ? '編集モードを終了' : '編集モード' }}
                </button>
                <!-- 未保存の変更があることを明示する(「保存」を押さない限りDBには反映されない) -->
                <span v-if="isDirty" class="pe-unsaved" title="「保存」を押すまでDBには反映されません">
                  未保存の変更あり
                </span>
              </div>

              <!-- Amazonカタログ更新・保存ボタン(カテゴリのブラウズノード編集バーと同じく、左寄せ・背景付きで配置)
                   NGワードのレベルフィルタは参照モードでも使うため、バー自体は常に表示する -->
              <div class="cat-edit-bar">
                <button
                  v-if="isEditMode"
                  class="btn btn-ghost btn-sm"
                  title="Amazonカタログの最新情報を取得し、画面上の入力内容を上書きします(保存ボタンを押すまでDBには反映されません)"
                  :disabled="refreshMutation.isPending.value"
                  @click="onRefresh"
                >
                  <svg class="icon icon-xs" viewBox="0 0 24 24"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>
                  {{ refreshMutation.isPending.value ? '取得中…' : 'Amazonから最新情報を取得' }}
                </button>
                <button v-if="isEditMode" class="btn btn-ghost btn-sm" :disabled="saveMutation.isPending.value" @click="onSave">
                  <svg class="icon icon-xs" viewBox="0 0 24 24"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>
                  {{ saveMutation.isPending.value ? '保存中…' : '保存' }}
                </button>
                <NgLevelFilter :ng-words="ngMark.ngWords" />
              </div>

              <!-- NGワードのヒット一覧(語のみ)。左のリストは説明文が長く目線移動が大きいため、
                   本文のすぐ上にも語だけを並べる。クリックで該当箇所へジャンプ＋ハイライト。
                   審査Webと同じ仕組み・見た目に揃えている。 -->
              <!-- 編集モード中は本文がtextareaになりハイライトできないため、
                   語を選んでも反応しない理由を明示する(旧ナビが担っていた案内) -->
              <div v-if="isEditMode && selectedNgWordName" class="ng-chip-row">
                <span class="ng-chip-label">NGワード</span>
                <span class="ng-chip-nav-count none">
                  編集モードではハイライトを表示できません（{{ selectedNgWordName }}）
                </span>
                <button class="ng-nav-close" title="選択を解除" @click="clearNgWordSelection">✕</button>
              </div>

              <div v-if="!isEditMode && ngWordChips.length" class="ng-chip-row">
                <span class="ng-chip-label">NGワード</span>
                <button
                  v-for="w in ngWordChips"
                  :key="w.word"
                  class="ng-chip"
                  :class="[`lv${levelBucket(w.level)}`, { active: selectedNgWordName === w.word }]"
                  :title="`Lv.${w.level}　クリックで本文の該当箇所へ移動`"
                  @click="toggleNgChip(w.word)"
                >{{ w.word }}</button>

                <span v-if="selectedNgWordName" class="ng-chip-nav">
                  <template v-if="matchCount > 0">
                    <span class="ng-chip-nav-count">{{ matchIndex + 1 }} / {{ matchCount }} 件</span>
                    <button class="ng-nav-btn" title="前のヒット箇所" @click="prevMatch">‹</button>
                    <button class="ng-nav-btn" title="次のヒット箇所" @click="nextMatch">›</button>
                  </template>
                  <span v-else class="ng-chip-nav-count none">本文にヒットなし</span>
                  <button class="ng-nav-close" title="選択を解除" @click="clearNgWordSelection">✕</button>
                </span>
              </div>

              <div class="field" style="margin-bottom:14px;">
                <label v-if="isEditMode">商品名</label>
                <input v-if="isEditMode" v-model="form.title" type="text" />
                <div v-else class="dh-title"><NgMarkedText :text="form.title || '未設定'" :ng-words="ngMark.ngWords" field="title" /></div>
              </div>
              <div v-if="!isEditMode" class="pe-title-divider"></div>

              <div class="attr-grid">
                <div class="field">
                  <label>カテゴリ <span v-if="isEditMode" style="color:var(--ng);">*</span></label>
                  <select v-if="isEditMode" v-model="form.categoryCode">
                    <option value="" disabled>選択してください</option>
                    <option v-for="c in categories" :key="c.CategoryCode" :value="c.CategoryCode">
                      {{ c.CategoryName }}
                    </option>
                  </select>
                  <div v-else class="v">{{ categoryName || '—' }}</div>
                </div>
                <div class="field">
                  <label>ブランド</label>
                  <input v-if="isEditMode" v-model="form.brand" type="text" />
                  <div v-else class="v">{{ form.brand || '—' }}</div>
                </div>
                <div class="field">
                  <label>モデル</label>
                  <input v-if="isEditMode" v-model="form.model" type="text" />
                  <div v-else class="v"><NgMarkedText :text="form.model || '—'" :ng-words="ngMark.ngWords" field="model" /></div>
                </div>
                <div class="field">
                  <label>カラー</label>
                  <input v-if="isEditMode" v-model="form.color" type="text" />
                  <div v-else class="v"><NgMarkedText :text="form.color || '—'" :ng-words="ngMark.ngWords" field="color" /></div>
                </div>
                <div class="field">
                  <label>サイズ</label>
                  <input v-if="isEditMode" v-model="form.size" type="text" />
                  <div v-else class="v"><NgMarkedText :text="form.size || '—'" :ng-words="ngMark.ngWords" field="size" /></div>
                </div>
                <div class="field">
                  <label>EAN</label>
                  <input v-if="isEditMode" v-model="form.ean" type="text" />
                  <div v-else class="v chip-id">{{ form.ean || '—' }}</div>
                </div>
                <div class="field">
                  <label>装丁</label>
                  <input v-if="isEditMode" v-model="form.binding" type="text" />
                  <div v-else class="v">{{ form.binding || '—' }}</div>
                </div>
                <div class="field">
                  <label>メーカー希望価格</label>
                  <input v-if="isEditMode" v-model="form.listPrice" type="number" />
                  <div v-else class="v">{{ form.listPrice || '—' }}</div>
                </div>
                <div class="field">
                  <label>発売日</label>
                  <input v-if="isEditMode" v-model="form.releaseDate" type="text" placeholder="yyyy-mm-dd" />
                  <div v-else class="v">{{ form.releaseDate || '—' }}</div>
                </div>
                <div class="field">
                  <label>Amazon販売ランキング</label>
                  <input v-if="isEditMode" v-model="form.salesRank" type="number" />
                  <div v-else class="v">{{ form.salesRank || '—' }}</div>
                </div>
                <div class="field">
                  <label>評価</label>
                  <input v-if="isEditMode" v-model="form.rating" type="number" step="0.1" />
                  <div v-else class="v">{{ form.rating || '—' }}</div>
                </div>
                <div class="field">
                  <label v-if="isEditMode" class="chk" style="margin-top:6px;">
                    <input type="checkbox" v-model="form.adultFlg" /> アダルト商品
                  </label>
                  <template v-else>
                    <label>アダルト商品</label>
                    <div class="v">{{ form.adultFlg ? 'はい' : 'いいえ' }}</div>
                  </template>
                </div>
              </div>

              <div class="field pe-longtext-field" :class="{ 'longtext-block': !isEditMode }">
                <label v-if="isEditMode">特徴</label>
                <div v-else class="k">特徴</div>
                <textarea v-if="isEditMode" v-model="form.features" rows="10"></textarea>
                <div v-else class="v v-readable"><NgMarkedText :text="form.features || '未設定'" :ng-words="ngMark.ngWords" preserve-whitespace field="features" /></div>
              </div>

              <div class="field pe-longtext-field" :class="{ 'longtext-block': !isEditMode }">
                <label v-if="isEditMode">商品説明</label>
                <div v-else class="k">商品説明</div>
                <textarea v-if="isEditMode" v-model="form.editorialReview" rows="16"></textarea>
                <div v-else class="v v-readable"><NgMarkedText :text="form.editorialReview || '未設定'" :ng-words="ngMark.ngWords" preserve-whitespace field="description" /></div>
              </div>

              <div class="attr-grid">
                <template v-if="isEditMode">
                  <div class="field"><label>長さ (cm)</label><input v-model="form.length" type="number" /></div>
                  <div class="field"><label>幅 (cm)</label><input v-model="form.width" type="number" /></div>
                  <div class="field"><label>高さ (cm)</label><input v-model="form.height" type="number" /></div>
                  <div class="field"><label>商品重量</label><input v-model="form.itemWeight" type="number" /></div>
                  <div class="field"><label>梱包重量</label><input v-model="form.packageWeight" type="number" /></div>
                </template>
                <template v-else>
                  <div class="attr-item"><span class="k">長さ×幅×高さ (cm)</span><span class="v">{{ form.length || '—' }} × {{ form.width || '—' }} × {{ form.height || '—' }}</span></div>
                  <div class="attr-item"><span class="k">商品重量</span><span class="v">{{ form.itemWeight || '—' }}</span></div>
                  <div class="attr-item"><span class="k">梱包重量</span><span class="v">{{ form.packageWeight || '—' }}</span></div>
                </template>
              </div>

              <div class="attr-grid" style="margin-top:14px;">
                <div class="attr-item"><span class="k">更新日時</span><span class="v">{{ product.UpdateDate ?? '—' }}</span></div>
                <div class="attr-item"><span class="k">更新者</span><span class="v">{{ product.UpdateUser ?? '—' }}</span></div>
              </div>
            </div>

            </div>


            </div>

            <!-- ジャッジ待ち申請(VB版 HyperlinkUncheckPicker 相当)。業務の流れ(編集→確認→
                 ジャッジ待ち申請)が分かるよう、商品情報カードの外側・下に独立配置する -->
            <div class="pe-apply-row">
              <button class="btn btn-primary" @click="showJudgmentQueueModal = true">
                <svg class="icon icon-xs" viewBox="0 0 24 24"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>
                この内容でジャッジ待ち申請
              </button>
            </div>
          </template>
        </div>
      </div>
    </div>

    <JudgmentQueueApplyModal
      v-if="showJudgmentQueueModal"
      :initial-asin="activeAsin"
      :region-code="regionCode"
      @close="showJudgmentQueueModal = false"
    />
  </div>
</template>

<style scoped>
#app-shell {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
}
/* 左ペインを畳んだときは、その分だけ必要な最小幅も下げる */
#app-shell.left-hidden {
  min-width: 760px;
}
/* 審査Webと同じ2カラム構成(.workspace/.col/.col-left/.col-center)を踏襲する */
.pe-left-scroll,
.pe-right-scroll {
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  height: 100%;
}
.pe-search-card {
  padding: 16px 18px;
  flex: none;
}

/* NGワードカードは縦方向いっぱいに広げ、できるだけスクロールせずに見えるようにする */
.pe-ngword-card {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}
.pe-ngword-section {
  /* グローバルの .ngword-section は審査Web下部の固定表示(sticky, height:42vh)を
     前提にした制約を持つが、この画面では左カラム内で縦いっぱいに広げたいため解除する */
  flex: 1;
  height: auto;
  max-height: none;
  min-height: 0;
  position: static;
  display: flex;
  flex-direction: column;
}
.pe-ngword-section .ngword-body {
  flex: 1;
  overflow-y: auto;
}

/* 項目名の色を審査Webの参照表示(.attr-item .k)と統一する(グレー→青)。
   .field label は他画面(FilterPanel.vue等)でも使われる共通クラスのため、
   scoped styleでこの画面内だけ上書きする(:deep()は子コンポーネント向けのため
   使わない。通常のscopedセレクタでこのコンポーネント内のみに限定する)。 */
.field label {
  color: var(--brand);
}

/* type=number の input は共通CSS(.field input[type=text], .field select)の
   対象外になっており、ブラウザ既定の見た目のまま他項目と印象が揃っていなかった。
   text/selectと同じ枠線・角丸・パディングにして統一する。 */
.field input[type=number] {
  border: 1px solid var(--line);
  border-radius: var(--radius-sm);
  padding: 6px 8px;
  font-size: 12.5px;
  color: var(--ink);
  background: var(--panel);
  outline: none;
  transition: border-color .12s;
}
.field input[type=number]:focus {
  border-color: var(--brand);
}

/* 表示モード時、商品名の下にカテゴリとの区切り線を引く */
.pe-title-divider {
  border-top: 1px solid var(--line);
  margin: 4px 0 14px;
}

/* 特徴・商品説明: 編集モードのtextareaを大きく・文字も一回り大きくして、
   できるだけスクロールせずに読み書きできるようにする。
   表示モードの外枠・改行保持は共通クラス(.longtext-block)に任せる。 */
.pe-longtext-field {
  margin-bottom: 14px;
}
.pe-longtext-field textarea {
  width: 100%;
  min-height: 320px;
  border: 1px solid var(--line);
  border-radius: var(--radius-sm);
  padding: 10px 12px;
  font-size: 14px;
  color: var(--ink);
  font-family: inherit;
  line-height: 1.7;
  resize: vertical;
}
.pe-longtext-field textarea:focus {
  outline: none;
  border-color: var(--brand);
  box-shadow: 0 0 0 3px var(--brand-tint);
}

/* ジャッジ待ち申請ボタン: 商品情報を編集→確認→申請、という業務の流れが
   一目で分かるよう、商品情報カードの外側・下に独立して配置する */
.pe-apply-row {
  display: flex;
  justify-content: center;
  padding: 4px 0 8px;
}
.pe-apply-row .btn {
  padding: 10px 28px;
  font-size: 13px;
}
</style>


